/**
 * Import Jobs Service
 * Real API implementation connecting to backend.
 *
 * List import jobs + poll a single job's status. Split out of
 * import.service.ts; bodies moved verbatim.
 */

import { api, throwBackendError } from '@/platform/api/api.service'
import { IMPORT_ROUTES } from '@/shared/constants/config-routes'
import { ApiException } from '@/shared/utils/api'

/**
 * Get import jobs for current user
 */
type ImportJob = Record<string, unknown>

export const getJobs = async (sourceId?: string): Promise<ImportJob[]> => {
  const url = sourceId ? `${IMPORT_ROUTES.JOBS.LIST}?sourceId=${sourceId}` : IMPORT_ROUTES.JOBS.LIST

  const response = await api.get<{
    jobs: unknown[]
    pagination: unknown
  }>(url)

  // Bubble backend errors so the caller can render a real error state instead of
  // an empty list — a 401/500 must NOT be indistinguishable from "no jobs".
  // (api.get never throws; it returns {success,error}. Mirrors getSources().)
  if (!response.success) {
    throwBackendError(response, 'IMPORT_JOBS_LOAD_FAILED')
  }

  // A successful response with an empty/absent payload legitimately means "no jobs".
  if (!response.data) {
    return []
  }

  // Jobs are in response.data.jobs or nested response.data.data.jobs
  // NOTE: Response shape varies by endpoint - handle both structures
  type JobsResponse = typeof response.data & { data?: { jobs?: unknown[] } }
  const nestedData = response.data as JobsResponse
  const jobs = nestedData.jobs || nestedData.data?.jobs
  return (Array.isArray(jobs) ? jobs : []) as ImportJob[]
}

/**
 * Get job status by ID
 */
export const getJobStatus = async (jobId: string): Promise<ImportJob> => {
  try {
    // apiService unwraps the envelope: api.get<T>() resolves to the payload (the `data` value). Never access .data on it.
    const response = await api.get<ImportJob>(IMPORT_ROUTES.JOBS.STATUS(jobId))

    // response.success is the envelope success flag; response.data IS the job payload.
    if (!response.success || !response.data) {
      throw new ApiException({
        status: 0,
        code: 'IMPORT_JOB_STATUS_FAILED',
        message: '',
      })
    }

    return response.data
  } catch (error) {
    throw error
  }
}
